'use client';

/* eslint-disable @next/next/no-img-element -- favicon/external/data-URI; next/image migration impractical for these dynamic logo/signature sources */
import { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { FormsTable } from '@/components/institutions/forms-table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { FileText, Upload, Plus, X } from 'lucide-react';
import { useInstitutionForms } from '@/lib/services/institution-forms/hooks';
import { useInstitution } from '@/lib/services/institutions/hooks';
import { mapFormResponseToForm } from '@/lib/services/institution-forms/types';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { acceptStringFor } from '@/lib/constants/file-type-accept';

interface ViewFormsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: number;
  institutionId: number;
  institutionName: string;
}

export function ViewFormsDialog({
  open,
  onOpenChange,
  matterId,
  institutionId,
  institutionName
}: ViewFormsDialogProps) {
  const [showUploadSection, setShowUploadSection] = useState(false);
  // Fetch institution data to get favicon
  const { data: institution } = useInstitution(institutionId, {
    enabled: open && !!institutionId
  });

  // Fetch forms for the institution using the hook
  const {
    data: formsData,
    isLoading,
    error,
    refetch: refetchForms
  } = useInstitutionForms(matterId, institutionId, undefined, {
    enabled: open && !!institutionId && !!matterId
  });

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error('Failed to load forms');
      console.error('Error loading forms:', error);
    }
  }, [error]);

  // Transform the forms response to the frontend format
  const forms = (formsData?.forms || []).map((form) =>
    mapFormResponseToForm(form, institutionId, matterId)
  );

  // Separate template forms from manually uploaded forms
  const templateForms = forms.filter((form) => form.templateId);
  const manualForms = forms.filter(
    (form) => !form.templateId && form.isFreeForm
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setShowUploadSection(false);
        onOpenChange(value);
      }}
    >
      <DialogContent className="flex max-h-[85vh] w-[90vw] max-w-5xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0 border-b pb-4">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
              {institution?.faviconUrl && (
                <img
                  src={institution.faviconUrl}
                  alt={`${institution?.name || institutionName} icon`}
                  className="h-5 w-5 object-contain"
                  onError={(e) => {
                    // Hide image if it fails to load
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              {institution?.name || institutionName} Forms
            </DialogTitle>
            {!showUploadSection && (
              <Button
                gap="loose"
                variant="outline"
                size="sm"
                onClick={() => setShowUploadSection(true)}
              >
                <Upload className="h-4 w-4" />
                Upload Manual Forms
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {isLoading ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-10 w-32" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="bg-error-subtle mb-4 rounded-full p-3">
                <FileText className="text-error-subtle-foreground h-6 w-6" />
              </div>
              <p className="text-muted-foreground text-sm font-medium">
                Failed to load forms
              </p>
              <p className="text-muted-foreground/70 mt-1 text-xs">
                Please try again later
              </p>
            </div>
          ) : forms.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="bg-muted mb-4 rounded-full p-3">
                <FileText className="text-muted-foreground h-6 w-6" />
              </div>
              <p className="text-muted-foreground text-sm font-medium">
                No forms on file for this institution
              </p>
              <p className="text-muted-foreground/70 mt-1 mb-6 text-xs">
                You can upload signed forms manually
              </p>
              <UploadFormsSection
                matterId={matterId}
                institutionId={institutionId}
                institutionName={institution?.name || institutionName}
                onUploadComplete={() => {
                  // Trigger refresh of forms data
                  refetchForms();
                }}
              />
            </div>
          ) : (
            <>
              {showUploadSection ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="mb-6">
                    <Button
                      gap="loose"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowUploadSection(false)}
                      className="mb-4"
                    >
                      <X className="h-4 w-4" />
                      Back to forms list
                    </Button>
                  </div>
                  <h3 className="mb-2 text-lg font-semibold">
                    Upload Manual Forms
                  </h3>
                  <p className="text-muted-foreground mb-6 text-sm">
                    Upload signed forms that you have received from{' '}
                    {institution?.name || institutionName}
                  </p>
                  <UploadFormsSection
                    matterId={matterId}
                    institutionId={institutionId}
                    institutionName={institution?.name || institutionName}
                    onUploadComplete={() => {
                      setShowUploadSection(false);
                      refetchForms();
                    }}
                  />
                </div>
              ) : (
                <>
                  {/* Show template forms if available */}
                  {templateForms.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="text-muted-foreground text-sm font-medium">
                        Institution Forms
                      </h3>
                      <FormsTable
                        forms={templateForms}
                        institutionName={institution?.name || institutionName}
                        matterId={matterId}
                        institutionId={institutionId}
                      />
                    </div>
                  )}

                  {/* Show manual forms if available */}
                  {manualForms.length > 0 && (
                    <div
                      className={cn(
                        'space-y-4',
                        templateForms.length > 0 && 'mt-8'
                      )}
                    >
                      <h3 className="text-muted-foreground text-sm font-medium">
                        Manually Uploaded Forms
                      </h3>
                      <FormsTable
                        forms={manualForms}
                        institutionName={institution?.name || institutionName}
                        matterId={matterId}
                        institutionId={institutionId}
                      />
                    </div>
                  )}

                  {/* If we only have forms but no breakdown, show all */}
                  {templateForms.length === 0 &&
                    manualForms.length === 0 &&
                    forms.length > 0 && (
                      <FormsTable
                        forms={forms}
                        institutionName={institution?.name || institutionName}
                        matterId={matterId}
                        institutionId={institutionId}
                      />
                    )}
                </>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Upload Forms Section Component
function UploadFormsSection({
  matterId,
  institutionId,
  institutionName: _institutionName,
  onUploadComplete
}: {
  matterId: number;
  institutionId: number;
  institutionName: string;
  onUploadComplete?: () => void;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    try {
      // Import the file upload client
      const { filesApi } = await import('@/lib/services/files/client');

      // Real aggregate upload progress across the batch (LF-4771) — average of
      // each file's live S3-PUT percent, via the shared primitive.
      const percents = new Array(selectedFiles.length).fill(0);
      const uploadPromises = selectedFiles.map(async (file, i) => {
        // Upload as institution-form-signed type with proper fields
        return filesApi.uploadFile(matterId, file, 'institution-form-signed', {
          displayName: file.name,
          institutionId: institutionId,
          // We don't have specific form IDs for manually uploaded forms
          // Backend should handle this as a generic signed form
          institutionFormId: undefined,
          signatoryContactId: undefined,
          onProgress: (pct) => {
            percents[i] = pct;
            setUploadProgress(
              Math.round(percents.reduce((a, b) => a + b, 0) / percents.length)
            );
          }
        });
      });

      // Wait for all uploads to complete
      await Promise.all(uploadPromises);

      toast.success(
        `Successfully uploaded ${selectedFiles.length} form${selectedFiles.length > 1 ? 's' : ''}`
      );
      setSelectedFiles([]);

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      toast.error('Failed to upload forms. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="w-full max-w-md">
      {/* File input (hidden) */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        // A signed institution form is a scan: the backend's `institution-form-signed`
        // takes a PDF or a photo and nothing else. This used to offer `.doc`, `.docx`,
        // `.xls` and `.xlsx`, all four of which it refuses, while omitting the image
        // formats it accepts (VALE-209).
        accept={acceptStringFor('institution-form-signed')}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload area */}
      <div
        className={cn(
          'cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors',
          'hover:border-primary/50 hover:bg-muted/50',
          isUploading && 'cursor-not-allowed opacity-50'
        )}
        onClick={() => !isUploading && fileInputRef.current?.click()}
      >
        <Upload className="text-muted-foreground mx-auto mb-3 h-8 w-8" />
        <p className="mb-1 text-sm font-medium">Click to upload forms</p>
        <p className="text-muted-foreground text-xs">
          PDF, DOC, DOCX, XLS, XLSX files accepted
        </p>
      </div>

      {/* Selected files list */}
      {selectedFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-sm font-medium">Selected files:</p>
          {selectedFiles.map((file, index) => (
            <div
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="bg-muted/50 flex items-center justify-between rounded px-3 py-2"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                <span className="truncate text-sm">{file.name}</span>
                <span className="text-muted-foreground text-xs">
                  ({(file.size / 1024).toFixed(1)} KB)
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon-2xs"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                disabled={isUploading}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}

          {/* Upload button */}
          <Button
            size="xs"
            density="compact"
            gap="standard"
            width="full"
            onClick={handleUpload}
            disabled={isUploading || selectedFiles.length === 0}
            className="mt-3"
          >
            {isUploading ? (
              <>
                <div className="border-card h-4 w-4 animate-spin rounded-full border-b-2" />
                {uploadProgress > 0
                  ? `Uploading ${uploadProgress}%`
                  : 'Uploading...'}
              </>
            ) : (
              <>
                <Upload className="h-3.5 w-3.5" />
                Upload {selectedFiles.length} file
                {selectedFiles.length > 1 ? 's' : ''}
              </>
            )}
          </Button>
        </div>
      )}

      {/* Add another file button */}
      {selectedFiles.length > 0 && !isUploading && (
        <Button
          size="xs"
          density="compact"
          gap="standard"
          width="full"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="mt-2"
        >
          <Plus className="h-3.5 w-3.5" />
          Add another file
        </Button>
      )}
    </div>
  );
}
